import dayjs from 'src/utils/dayjs';
import { calculateActivityValue } from 'src/utils/activities';

import { allNeighbourhood } from 'src/pages/procurer/preferences/Geography/data';

import type { TenderSubmissionData } from './types';

export const fmt = (date?: string) => (date ? dayjs(date).format('DD MMM YYYY') : '—');
export const fmtTimestamp = (date?: string) =>
  date ? dayjs(date).format('DD MMM YYYY HH:mm') : '—';
export const fmtCurrency = (val: number) =>
  `£${val.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// Strip HTML tags and decode common entities — these fields are stored as rich text
export const stripHtml = (html?: string) =>
  (html ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s{2,}/g, ' ')
    .trim();

const areaLabel = (value: string) =>
  allNeighbourhood.find((n) => n.value === value)?.label ?? value;

export function triggerPdfDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function pdfFilename(contractTitle: string) {
  return `tender-submission-${contractTitle.replace(/\s+/g, '-').toLowerCase()}.pdf`;
}

function calcSupplierSummary(commitToActivities: any[]) {
  const reduce = (field: string) =>
    (commitToActivities ?? []).reduce(
      (sum: number, a: any) =>
        sum +
        calculateActivityValue(a[field], {
          units: a.units,
          committingPointsValue: a.committingPointsValue,
          committingMarketValue: a.committingMarketValue,
          committingImpactProxyValue: a.committingImpactProxyValue,
        }),
      0
    );

  return {
    points: reduce('committingPointsCalculation'),
    marketValue: reduce('committingMarketCalculation'),
    impactValue: reduce('committingImpactProxyCalculation'),
  };
}

export function buildSupplierPdfData(data: any): TenderSubmissionData {
  const contract = data?.contract ?? {};
  const qualitativeQuestions: any[] = contract.qualitativeQuestions ?? [];
  const storedAnswers: any[] = data?.submissionQuestionsAnswers ?? [];

  return {
    contractTitle: contract.title ?? '',
    contractType: contract.type,
    contractStartDate: contract.startDate,
    contractEndDate: contract.endDate,
    applicationStartDate: contract.applicationStartDate,
    applicationEndDate: contract.applicationEndDate,
    contractLeadName: data?.contractLeadName,
    contractLeadEmail: data?.contractLeadEmail,
    socialValueLeadName: data?.socialValueLeadName,
    socialValueLeadEmail: data?.socialValueLeadEmail,
    applicationStatus: data?.status,
    generatedBy: 'Supplier view',
    priorityAreas: (contract.priorityGeographicalAreas ?? []).map(areaLabel),
    otherAreas: (contract.geographicalAreas ?? []).map(areaLabel),
    themes: contract.themes ?? [],
    applicationOverview: contract.applicationOverview,
    instructionsOverview: contract.instructionsOverview,
    additionalInformation: contract.additionalInformation,
    brokerageSystem: contract.haveBrokerageSystem ? contract.brokerageSystem : undefined,
    documents: (contract.documents ?? []).map((d: any) => ({
      title: d.title ?? d.originalName ?? '',
      url: d.url ?? d.downloadUrl,
    })),
    supplierName: data?.supplierName,
    supplierOrgName: data?.supplierOrgName,
    activities: (data?.commitToActivities ?? []).map((a: any) => ({
      title: a.title,
      units: a.units,
      additionalQA: (a.additionalQuestions ?? []).map((q: any) => ({
        question: q.question ?? q.text ?? '',
        answer: q.answer ?? '',
      })),
    })),
    commitmentSummary: calcSupplierSummary(data?.commitToActivities ?? []),
    qualitativeQA:
      qualitativeQuestions.length > 0
        ? qualitativeQuestions.map((q: any) => ({
            question: q.title ?? '',
            answer:
              storedAnswers.find(
                (a: any) =>
                  (a.id && a.id === q.id) ||
                  (a.title && a.title === q.title) ||
                  (a.question && (a.question === q.title || a.question === q.helperText))
              )?.answer ?? '',
          }))
        : storedAnswers.map((a: any) => ({
            question: a.question ?? a.title ?? '',
            answer: a.answer ?? '',
          })),
  };
}

export function buildProcurerPdfData(data: any): TenderSubmissionData {
  const contract = data?.contract ?? {};
  const qualitativeQuestions: any[] = contract.qualitativeQuestions ?? [];
  const submissionQuestionsAnswers: any[] = data?.submissionQuestionsAnswers ?? [];

  return {
    contractTitle: contract.title ?? '',
    contractType: contract.type,
    contractStartDate: contract.startDate,
    contractEndDate: contract.endDate,
    applicationStartDate: contract.applicationStartDate,
    applicationEndDate: contract.applicationEndDate,
    supplierName: data?.supplier?.name,
    supplierOrgName: data?.supplier?.organisationName,
    contractLeadName: data?.contractLeadName,
    contractLeadEmail: data?.contractLeadEmail,
    socialValueLeadName: data?.socialValueLeadName,
    socialValueLeadEmail: data?.socialValueLeadEmail,
    applicationStatus: data?.status,
    generatedBy: 'Procurer view',
    priorityAreas: (contract.priorityGeographicalAreas ?? []).map(areaLabel),
    otherAreas: (contract.geographicalAreas ?? []).map(areaLabel),
    themes: contract.themes ?? [],
    applicationOverview: contract.applicationOverview,
    instructionsOverview: contract.instructionsOverview,
    additionalInformation: contract.additionalInformation,
    brokerageSystem: contract.haveBrokerageSystem ? contract.brokerageSystem : undefined,
    documents: (contract.documents ?? []).map((d: any) => ({
      title: d.title ?? d.originalName ?? '',
      url: d.url ?? d.downloadUrl,
    })),
    activities: (data?.activities ?? []).map((a: any) => ({
      code: a.code,
      title: a.title,
      units: a.units,
      additionalQA: (a.additionalQuestions ?? []).map((q: any) => ({
        question: q.question ?? q.text ?? '',
        answer: q.answer ?? '',
      })),
    })),
    commitmentSummary: data?.summary
      ? {
          points: data.summary.points ?? 0,
          marketValue: data.summary.marketValue ?? 0,
          impactValue: data.summary.impactValue ?? 0,
        }
      : undefined,
    qualitativeQA:
      qualitativeQuestions.length > 0
        ? qualitativeQuestions.map((q: any) => ({
            question: q.title ?? '',
            answer:
              submissionQuestionsAnswers.find(
                (a: any) =>
                  (a.id && a.id === q.id) ||
                  (a.title && a.title === q.title) ||
                  (a.question && (a.question === q.title || a.question === q.helperText))
              )?.answer ?? '',
          }))
        : submissionQuestionsAnswers.map((a: any) => ({
            question: a.question ?? a.title ?? '',
            answer: a.answer ?? '',
          })),
  };
}
